/**
 * @fileoverview Mapeo de estados de chat para Inbox, utilizado para mostrar el estado visual y textual en componentes.
 * @module statusChatMap
 * @category Utilidad
 * @since 2.0.0
 * @version 3.1.0
 *
 * @typedef {Object} StatusChatItem
 * @property {string} value - Valor textual del estado ('new', 'open', 'pending', 'resolved')
 * @property {string} letter - Letra representativa del estado para el avatar
 * @property {string} classNameAvatarMC - Clases TailwindCSS para el avatar según estado
 *
 * @typedef {Object} StatusChatMap
 * @description Mapeo de estados de chat por código numérico
 * @property {StatusChatItem} 0 - Estado 'new' (Nuevo)
 * @property {StatusChatItem} 1 - Estado 'open' (Abierto)
 * @property {StatusChatItem} 2 - Estado 'pending' (Pendiente)
 * @property {StatusChatItem} 3 - Estado 'resolved' (Resuelto)
 *
 * @constant
 * @type {StatusChatMap}
 * @description
 * Mapa utilizado para obtener información visual y textual del estado de un chat en el inbox.
 *
 * @example
 * import STATUS_CHAT_MAP from './statusChatMap';
 * const estado = STATUS_CHAT_MAP[1];
 * // estado.value => 'open'
 * // estado.letter => 'A'
 * // estado.classNameAvatarMC => 'tw-bg-blue-50 tw-text-blue-500'
 *
 * @see {@link presentation/components/AvatarMC} Para uso en avatares de chat
 */
const STATUS_CHAT_MAP = {
  0: {
    value: 'new',
    letter: 'N',
    classNameAvatarMC: 'tw-bg-vermilion-50 tw-text-vermilion-700',
    status: 'new_chat',
    status_interaction: 'new_interaction'
  },
  1: {
    value: 'open',
    letter: 'A',
    classNameAvatarMC: 'tw-bg-blue-50 tw-text-blue-500',
    status: 'open_chat',
    status_interaction: 'open_interaction'
  },
  2: {
    value: 'pending',
    letter: 'P',
    classNameAvatarMC: 'tw-bg-orange-50 tw-text-orange-400',
    status: 'in_process',
    status_interaction: 'in_process_interaction'
  },
  3: {
    value: 'resolved',
    letter: 'R',
    classNameAvatarMC: 'tw-bg-green-50 tw-text-green-400',
    status: 'resolved',
    status_interaction: 'resolved_interaction'
  }
};

const STATUS_KEYS = Object.keys(STATUS_CHAT_MAP).map(Number);

/**
 * Normaliza el estado de chat a su clave numérica (0-3).
 * Si el valor no es numérico (ej. 'new', 'open', 'pending', 'resolved'), busca por value y devuelve el número correspondiente.
 * @param {number|string} status - Código numérico o valor textual del estado
 * @returns {number|null} Clave numérica (0-3) o null si no hay coincidencia
 */
export const getStatusNumber = (status) => {
  const str = String(status);
  const asNumber = Number(str);
  if (!Number.isNaN(asNumber) && STATUS_KEYS.includes(asNumber)) {
    return asNumber;
  }
  const entry = Object.entries(STATUS_CHAT_MAP).find(
    ([, item]) => item.value === str || item.status === str || item.status_interaction === str
  );
  return entry ? Number(entry[0]) : null;
};

export default STATUS_CHAT_MAP;
